import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";

import {
  api,
  impersonation,
  unwrap,
  type AdminOrganization,
  type ImpersonationSession,
  type OrganizationStatus,
  type PlatformMetrics,
} from "./client";

/**
 * Every call the staff console can make. Six, because the contract defines six — there is
 * no seventh, hand-written, "just for the console".
 */
export const keys = {
  organizations: (search: string, status: string) => ["admin", "organizations", search, status] as const,
  metrics: () => ["admin", "metrics"] as const,
};

export interface OrgListResult {
  data: AdminOrganization[];
  hasMore: boolean;
}

/** GET /v1/admin/organizations — the one read in the product that spans every tenant. */
export function useOrganizations(search: string, status: string): UseQueryResult<OrgListResult, Error> {
  return useQuery({
    queryKey: keys.organizations(search, status),
    queryFn: async () => {
      const result = await unwrap(
        api.GET("/v1/admin/organizations", {
          params: {
            query: {
              ...(search ? { q: search } : {}),
              ...(status ? { status: status as OrganizationStatus } : {}),
            },
          },
        }),
      );
      return { data: result.data, hasMore: result.page.has_more };
    },
  });
}

/** GET /v1/admin/metrics — aggregates only. Nothing here names a tenant. */
export function useMetrics(): UseQueryResult<PlatformMetrics, Error> {
  return useQuery({
    queryKey: keys.metrics(),
    queryFn: () => unwrap(api.GET("/v1/admin/metrics")),
  });
}

/** POST /v1/admin/organizations/{orgId}/suspend */
export function useSuspend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, reason }: { orgId: string; reason: string }) =>
      unwrap(
        api.POST("/v1/admin/organizations/{orgId}/suspend", {
          params: { path: { orgId } },
          body: { reason },
        }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin"] }),
  });
}

/** POST /v1/admin/organizations/{orgId}/unsuspend */
export function useUnsuspend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, reason }: { orgId: string; reason: string }) =>
      unwrap(
        api.POST("/v1/admin/organizations/{orgId}/unsuspend", {
          params: { path: { orgId } },
          body: { reason },
        }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin"] }),
  });
}

/**
 * DELETE /v1/admin/organizations/{orgId} — irreversible, cascading.
 *
 * The server refuses it unless the organization is ALREADY SUSPENDED (409), which is the
 * pause between deciding and doing. The console asks for the name to be typed out as well —
 * see DeleteOrganizationDialog — but that is the SECOND lock, not the first. The first one
 * is in the domain, where a UI cannot skip it.
 */
export function useDeleteOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, reason }: { orgId: string; reason: string }) =>
      unwrap(
        api.DELETE("/v1/admin/organizations/{orgId}", {
          params: { path: { orgId } },
          body: { reason },
        }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin"] }),
  });
}

/**
 * POST /v1/admin/impersonate — mints a READ-ONLY session inside the customer's tenant.
 *
 * The token is put in sessionStorage, not localStorage: closing the tab ends it. The banner
 * is then unmissable and persistent for as long as it lives, because a support engineer who
 * forgets they are impersonating is the whole risk this feature carries.
 */
export function useImpersonate() {
  return useMutation({
    mutationFn: ({ orgId, reason, userId }: { orgId: string; reason: string; userId?: string }) =>
      unwrap(
        api.POST("/v1/admin/impersonate", {
          body: {
            organization_id: orgId,
            reason,
            ...(userId ? { user_id: userId } : {}),
          },
        }),
      ),
    onSuccess: (session: ImpersonationSession) => {
      impersonation.set({
        accessToken: session.access_token,
        organizationId: session.organization.id,
        organizationName: session.organization.name,
        userName: session.impersonated_user?.name ?? "a member",
        expiresAt: session.expires_at,
        auditId: session.audit_id,
      });
    },
  });
}
